/* eslint-disable */

const calcFirstChecker = (firstNineDigits: string): number => {
  let sum = 0

  for (let j = 0; j < 9; ++j) {
    sum += Number(firstNineDigits.toString().charAt(j)) * (10 - j)
  }

  const lastSumChecker1 = sum % 11
  const checker1 = lastSumChecker1 < 2 ? 0 : 11 - lastSumChecker1

  return checker1
}

const calcSecondChecker = (cpfWithChecker1: string): number => {
  let sum = 0

  for (let k = 0; k < 10; ++k) {
    sum += Number(cpfWithChecker1.toString().charAt(k)) * (11 - k)
  }

  const lastSumChecker2 = sum % 11
  const checker2 = lastSumChecker2 < 2 ? 0 : 11 - lastSumChecker2

  return checker2
}

// get length in type-agnostic way
const len = (value: any) => {
  if (Array.isArray(value)) return value.length
  if (typeof value === 'object') {
    return Object.keys(value).length
  }
  return String(value).length
}

export const validateCPF = (value: string | number): boolean => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    console.warn('Unsupported value')
    return false
  }

  const cleanCPF = String(value).replace(/\.|-|\s/g, '')
  const firstNineDigits = cleanCPF.substring(0, 9)
  const checker = cleanCPF.substring(9, 11)

  if (cleanCPF.length !== 11) {
    return false
  }

  // Checking if all digits are equal
  for (let i = 0; i < 10; i++) {
    if (`${firstNineDigits}${checker}` === Array(12).join(String(i))) {
      return false
    }
  }

  const checker1 = calcFirstChecker(firstNineDigits)
  const checker2 = calcSecondChecker(`${firstNineDigits}${checker1}`)

  return checker.toString() === checker1.toString() + checker2.toString()
}

/**
 * CNPJ alfanumérico — Nota Técnica COCAD/SUARA/RFB nº 49/2024.
 *
 * Estrutura (14 caracteres): raiz (8) + ordem (4) + DV (2).
 * Os 12 primeiros caracteres aceitam [A-Z0-9]; os 2 dígitos verificadores são
 * sempre numéricos. O DV é calculado pelo módulo 11, substituindo cada caractere
 * pelo seu código ASCII menos 48 ('0' = 0 ... '9' = 9, 'A' = 17 ... 'Z' = 42).
 *
 * Como '0'-'9' mantêm o valor 0-9 e os pesos são os mesmos, o CNPJ numérico
 * antigo continua válido por construção.
 */
const CNPJ_LENGTH = 14
const CNPJ_BASE_LENGTH = 12
const CNPJ_CHECK_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const CNPJ_CHAR_VALUE_BASE = '0'.charCodeAt(0)
const CNPJ_ZEROED = '0'.repeat(CNPJ_LENGTH)
// Aceita apenas ASCII antes do toUpperCase, para que caracteres como 'ı'
// não sejam normalizados em letras válidas.
const CNPJ_ALLOWED_INPUT = /^[A-Za-z\d./-]+$/
const CNPJ_MASK_CHARS = /[./-]/g
const CNPJ_BASE_PATTERN = /^[A-Z\d]{12}$/
const CNPJ_PATTERN = /^[A-Z\d]{12}\d{2}$/

const normalizeCNPJ = (value: string | number): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null
  }

  const trimmed = String(value).trim()

  if (!CNPJ_ALLOWED_INPUT.test(trimmed)) {
    return null
  }

  return trimmed.toUpperCase().replace(CNPJ_MASK_CHARS, '')
}

export function calculateCNPJCheckDigits(base: string | number): string | null {
  const numbers = normalizeCNPJ(base)

  if (
    numbers === null
    || !CNPJ_BASE_PATTERN.test(numbers)
    || numbers === CNPJ_ZEROED.substring(0, CNPJ_BASE_LENGTH)
  ) {
    return null
  }

  let firstSum = 0
  let secondSum = 0

  for (let i = 0; i < CNPJ_BASE_LENGTH; i++) {
    const charValue = numbers.charCodeAt(i) - CNPJ_CHAR_VALUE_BASE

    firstSum += charValue * CNPJ_CHECK_DIGIT_WEIGHTS[i + 1]
    secondSum += charValue * CNPJ_CHECK_DIGIT_WEIGHTS[i]
  }

  const firstDigit = firstSum % 11 < 2 ? 0 : 11 - (firstSum % 11)

  secondSum += firstDigit * CNPJ_CHECK_DIGIT_WEIGHTS[CNPJ_BASE_LENGTH]

  const secondDigit = secondSum % 11 < 2 ? 0 : 11 - (secondSum % 11)

  return `${firstDigit}${secondDigit}`
}

export function validateCNPJ(cnpj: string | number): boolean {
  const numbers = normalizeCNPJ(cnpj)

  if (numbers === null || !CNPJ_PATTERN.test(numbers) || numbers === CNPJ_ZEROED) {
    return false
  }

  const informedCheckDigits = numbers.substring(CNPJ_BASE_LENGTH)
  const calculatedCheckDigits = calculateCNPJCheckDigits(numbers.substring(0, CNPJ_BASE_LENGTH))

  return informedCheckDigits === calculatedCheckDigits
}

export const required = (value: any) => {
  if (Array.isArray(value)) return !!value.length
  if (value === undefined || value === null) {
    return false
  }

  if (value === false) {
    return true
  }

  if (value instanceof Date) {
    // invalid date won't pass
    return !isNaN(value.getTime())
  }

  if (typeof value === 'object') {
    for (const _ in value) return true
    return false
  }

  return !!String(value).length
}

export const email = (value: string): boolean => {
  const emailRegex = /(^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/

  return !required(value) || emailRegex.test(value)
}

export const minLength = (length: number, value: any): boolean => {
  return !required(value) || len(value) >= length
}

export const isValidDate = (value: string): boolean => {
  const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/

  if (!dateRegex.test(value)) {
    return false
  }

  const [day, month, year] = value.split('/').map(part => parseInt(part, 10))
  const date = new Date(year, (month - 1), day)

  return (
    (date.getFullYear() === year) &&
    (date.getMonth() === (month - 1)) &&
    (date.getDate() === day)
  )
}
